import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { ArrowDropDownRounded, DatasetRounded, NotListedLocationRounded } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import { memo, useEffect } from 'react'
import { INodeReportPlaceholderColumn } from '../../../../../definitions/Nodes'
import NodeCodemirrorTextEditor from '../../NodeCodemirrorTextEditor'
import { useDebouncedCommit } from './NodeReportEditor'

interface P {
    column: INodeReportPlaceholderColumn
    columnIdx: number
    placeholderIdx: number
    disable: boolean
    onColumnPlaceholderChange: (placeholderIdx: number, columnIdx: number, val: string) => void
    shape: string[]
    globalsSuggestions: Completion[]
}

const ColumnPlaceholder = ({ column, columnIdx, placeholderIdx, disable, shape, onColumnPlaceholderChange, globalsSuggestions }: P) => {
    const commit = (val: string) => {
        onColumnPlaceholderChange(placeholderIdx, columnIdx, val)
    }

    const [input, setInput] = useDebouncedCommit(column?.value ?? '', commit, 500)

    useEffect(() => {
        if (!column?.value && shape.includes(column.name) && input !== column.name) {
            setInput(column.name)
        }
    }, [])

    const suggestions: Completion[] = shape.length > 0
        ? shape.map(x => ({ label: x, type: 'fieldInput', info: '-> Field Input' }))
        : []

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return { from: word.from, options: [...suggestions, ...globalsSuggestions] }
    }

    return (
        <Stack flexDirection='row' alignItems='center' gap={1}>
            <Stack flexDirection='row' alignItems='center' width={170} gap={1}>
                <IconButton size='small' sx={{ visibility: 'hidden' }}>
                    <ArrowDropDownRounded fontSize='small' />
                </IconButton>
                <DatasetRounded fontSize='small' sx={{ visibility: 'hidden' }} />
                <Typography variant='subtitle2'>{column.name}</Typography>
            </Stack>
            <Stack flexDirection='row' flex={1} overflow='hidden'>
                <NodeCodemirrorTextEditor
                    value={input}
                    disabled={disable}
                    placeholder='value'
                    style={{ overflow: 'hidden', minWidth: '100%' }}
                    onChange={value => setInput(value)}
                    extensions={[autocompletion({ override: [myCompletions] })]}
                />
            </Stack>
            <NotListedLocationRounded fontSize='small' sx={{ visibility: 'hidden' }} />
        </Stack>
    )
}

export default memo(ColumnPlaceholder)
